import { cn } from '@tempo-io/sui-class-utils';
import { Info, Warning } from '@tempo-io/sui-icons';
import { Separator } from '@tempo-io/sui-separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { SchedulingValidation } from '@/lib/api/types/adaptive-planner/scenario-item';
import { formatLocalDate } from '@/lib/utils/common/dateFormats';
import { getSchedulingValidationMessage } from './getSchedulingValidationMessage';
import { TooltipScheduleBar } from './TooltipScheduleBar';

const FieldInfoTooltip = ({ content }: { content: string }) => (
  <Tooltip>
    <TooltipTrigger className="flex items-center">
      <Info className="w-3.5 h-3.5 text-text-neutral-secondary hover:text-text-neutral-primary" />
    </TooltipTrigger>
    <TooltipContent className="max-w-56">{content}</TooltipContent>
  </Tooltip>
);

interface TooltipDatesSectionProps {
  forecastedStart?: string;
  forecastedFinish?: string;
  latestFinish?: string;
  isAtRisk: boolean;
  name: string;
  schedulingValidations?: SchedulingValidation[] | null;
  showFieldTooltips?: boolean;
}

export const TooltipDatesSection = ({
  forecastedStart,
  forecastedFinish,
  latestFinish,
  isAtRisk,
  name,
  schedulingValidations,
  showFieldTooltips = false,
}: TooltipDatesSectionProps) => {
  const { t } = useTranslation();
  const isEmpty = isAtRisk && !forecastedStart && !forecastedFinish && !latestFinish;

  return (
    <div className="space-y-2">
      {forecastedStart && forecastedFinish && (
        <TooltipScheduleBar
          forecastedStart={forecastedStart}
          forecastedFinish={forecastedFinish}
          latestFinish={latestFinish}
          isAtRisk={isAtRisk}
        />
      )}
      <div className="flex justify-between">
        {forecastedStart && (
          <div className="flex flex-col">
            <span className="flex items-center gap-1 text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.forecastedStart)}:
              {showFieldTooltips && (
                <FieldInfoTooltip
                  content={t(($) => $.adaptivePlanner.scenarioDetails.forecastedStartTooltip)}
                />
              )}
            </span>
            <span className="text-xs text-text-neutral-secondary">
              {formatLocalDate(parseISO(forecastedStart).toISOString())}
            </span>
          </div>
        )}

        {forecastedFinish && (
          <div className="flex flex-col items-end">
            <span className="flex items-center gap-1 text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.forecastedFinish)}:
              {showFieldTooltips && (
                <FieldInfoTooltip
                  content={t(($) => $.adaptivePlanner.scenarioDetails.forecastedFinishTooltip)}
                />
              )}
            </span>
            <span className="text-xs text-text-neutral-secondary">
              {formatLocalDate(parseISO(forecastedFinish).toISOString())}
            </span>
          </div>
        )}
      </div>
      {latestFinish && (
        <div className="flex w-full justify-end">
          <div className="flex flex-col items-end">
            <span className="flex items-center gap-1 text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.latestFinish)}:
              {showFieldTooltips && (
                <FieldInfoTooltip
                  content={t(($) => $.adaptivePlanner.scenarioDetails.latestFinishTooltip)}
                />
              )}
            </span>
            <span className={cn('text-xs', isAtRisk && 'text-text-error')}>
              {formatLocalDate(parseISO(latestFinish).toISOString())}
            </span>
          </div>
        </div>
      )}
      {isAtRisk && latestFinish && <Separator />}
      {isAtRisk && (
        <div className="flex items-start gap-3">
          <Warning className="h-4 w-4 text-text-error shrink-0 pt-1" />
          {isEmpty && schedulingValidations != null && schedulingValidations.length > 0 ? (
            <ul className="text-sm text-text-error space-y-1 list-none">
              {schedulingValidations.map((validation) => (
                <li className="text-wrap" key={validation}>
                  {getSchedulingValidationMessage(validation, t)}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-sm text-text-error">
              {isEmpty
                ? t(($) => $.adaptivePlanner.scenarioDetails.table.scheduleBar.tooltip.empty)
                : t(($) => $.adaptivePlanner.scenarioDetails.table.scheduleBar.tooltip.warning, {
                    name,
                  })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
